import React, { useRef } from 'react';
import { Box, Typography, Button, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  ChevronLeft, 
  ChevronRight, 
  Scale, 
  FileText, 
  Search, 
  FileCheck, 
  CalendarClock, 
  Shield, 
  Landmark,
  BookOpen
} from 'lucide-react';

const ACCENT_COLOR = '#007BFE';
const HOVER_COLOR = '#0069d9';
const NOT_AVAILABLE_COLOR = '#9e9e9e'; // Color gris para botones no disponibles

// Contenedor de características con scroll horizontal
const FeaturesSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(0),
  marginBottom: theme.spacing(15),
  overflow: 'visible', // Permitir el desbordamiento
  width: '100%',
  
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(12),
  },
}));

const FeaturesSectionTitle = styled(Typography)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    fontSize: '42px',
    fontWeight: 600,
    color: isWallpaperMode ? '#FFFFFF' : (theme.palette.mode === 'dark' ? '#ffffff' : '#1d1d1f'),

    [theme.breakpoints.down('md')]: {
      fontSize: '36px',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '28px',
    },
  };
});

// Ajustamos el contenedor de tarjetas para mantener el desborde en todas las pantallas
const FeatureCardsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  overflowX: 'auto',
  gap: theme.spacing(4),
  paddingTop: theme.spacing(2), // Añadimos padding superior para el hover
  paddingBottom: theme.spacing(4), // Aumentamos el padding inferior para el hover
  scrollSnapType: 'x mandatory',
  WebkitOverflowScrolling: 'touch',
  msOverflowStyle: 'none', // IE and Edge
  scrollbarWidth: 'none', // Firefox
  '&::-webkit-scrollbar': {
    display: 'none', // Chrome, Safari, Opera
  },
  // Importante: permitir que se extienda más allá del contenedor
  width: '100vw', // Ancho total de la ventana
  position: 'relative',
  left: '-140px', // Compensamos el margen izquierdo de 140px para que se desborde por la izquierda
  paddingLeft: '140px', // Añadimos padding para que la primera tarjeta se alinee correctamente
  
  // Ajustes responsivos para mantener el desborde en pantallas pequeñas
  [theme.breakpoints.down('md')]: {
    left: theme.spacing(-3), // Alineado con el padding izquierdo en tablet
    paddingLeft: theme.spacing(3),
    gap: theme.spacing(3),
  },
  [theme.breakpoints.down('sm')]: {
    left: theme.spacing(-2), // Alineado con el padding izquierdo en móvil
    paddingLeft: theme.spacing(2),
    gap: theme.spacing(2),
    paddingRight: theme.spacing(2), // Añadir padding derecho en móvil
  },
}));

// FeatureCard actualizado para ser responsivo y sin sombras
const FeatureCard = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    flexShrink: 0,
    scrollSnapAlign: 'center',
    width: '316px',
    height: '242px',
    borderRadius: '16px',
    padding: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start', // Alineamos todo a la izquierda
    boxShadow: 'none',
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : (theme.palette.mode === 'dark' ? '#1c1c1e' : '#f2f2f7'),
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    border: isWallpaperMode
      ? '1px solid rgba(128, 128, 128, 0.3)'
      : `1px solid ${theme.palette.mode === 'dark' ? '#1c1c1e' : '#f2f2f7'}`,
    position: 'relative',
    transition: 'transform 0.3s ease',
    margin: '2px', // Añadimos un pequeño margen para evitar que el hover se corte

    '&:hover': {
      transform: 'translateY(-2px)',
      backgroundColor: isWallpaperMode
        ? 'rgba(128, 128, 128, 0.5)'
        : (theme.palette.mode === 'dark' ? '#1c1c1e' : '#f2f2f7'),
    },

    [theme.breakpoints.down('md')]: {
      width: '290px',
      height: '230px',
      padding: theme.spacing(3.5),
    },
    [theme.breakpoints.down('sm')]: {
      width: '260px',
      height: '220px',
      padding: theme.spacing(3),
    },
  };
});

// ActionCard actualizado con imagen de fondo y sin sombra inicial
const ActionCard = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    flexShrink: 0,
    scrollSnapAlign: 'center',
    width: '316px',
    height: '242px',
    borderRadius: '16px',
    padding: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    boxShadow: 'none',
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : (theme.palette.mode === 'dark' ? '#1c1c1e' : '#f2f2f7'),
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    border: isWallpaperMode
      ? '1px solid rgba(128, 128, 128, 0.3)'
      : `1px solid ${theme.palette.mode === 'dark' ? '#1c1c1e' : '#f2f2f7'}`,
    position: 'relative',
    backgroundImage: 'url("https://agtupsproductsimages.s3.us-east-2.amazonaws.com/gallery/Mockup+Active+or+Buy+(Legal).png")',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    margin: '2px', // Añadimos un pequeño margen para evitar que el hover se corte

    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
      backgroundColor: isWallpaperMode
        ? 'rgba(128, 128, 128, 0.5)'
        : (theme.palette.mode === 'dark' ? '#1c1c1e' : '#f2f2f7'),
    },

    [theme.breakpoints.down('md')]: {
      width: '290px',
      height: '230px',
      padding: theme.spacing(3.5),
    },
    [theme.breakpoints.down('sm')]: {
      width: '260px',
      height: '220px',
      padding: theme.spacing(3),
    },
  };
});

const ActionCardHeader = styled(Box)({
  marginBottom: '16px',
  position: 'relative',
  zIndex: 1,
});

const ActionCardFooter = styled(Box)({
  marginTop: 'auto',
  position: 'relative',
  zIndex: 1,
});

const ButtonContent = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: '30px',
  padding: '10px 20px',
  fontWeight: 600,
  textTransform: 'none',
  fontSize: '0.875rem',
  backgroundColor: ACCENT_COLOR,
  color: '#ffffff',
  '&:hover': {
    backgroundColor: HOVER_COLOR,
  },
  marginTop: '16px',
  
  [theme.breakpoints.down('sm')]: {
    padding: '8px 16px',
    fontSize: '0.8rem',
    marginTop: '12px',
  },
}));

// Botón para servicios no disponibles
const NotAvailableButton = styled(Button)(({ theme }) => ({
  borderRadius: '30px',
  padding: '10px 20px',
  fontWeight: 600,
  textTransform: 'none',
  fontSize: '0.875rem',
  backgroundColor: NOT_AVAILABLE_COLOR,
  color: '#ffffff',
  '&:hover': {
    backgroundColor: '#757575', // Un tono más oscuro del gris
  },
  marginTop: '16px',
  
  [theme.breakpoints.down('sm')]: {
    padding: '8px 16px',
    fontSize: '0.8rem',
    marginTop: '12px',
  },
}));

const ProStatusButton = styled(Button)(({ theme }) => ({
  borderRadius: '30px',
  padding: '10px 20px',
  fontWeight: 600,
  textTransform: 'none',
  fontSize: '0.875rem',
  backgroundColor: ACCENT_COLOR,
  color: '#ffffff',
  cursor: 'default',
  '&:hover': {
    backgroundColor: ACCENT_COLOR,
  },
  marginTop: '16px',
  
  [theme.breakpoints.down('sm')]: {
    padding: '8px 16px',
    fontSize: '0.8rem',
    marginTop: '12px',
  },
}));

const FeatureCardTitle = styled(Typography)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    fontWeight: 600,
    fontSize: '1.5rem',
    textAlign: 'left',
    color: isWallpaperMode ? '#FFFFFF' : (theme.palette.mode === 'dark' ? '#ffffff' : '#1d1d1f'),

    [theme.breakpoints.down('md')]: {
      fontSize: '1.375rem',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.25rem',
    },
  };
});

// Actualizamos el FeatureIcon para alinearlo a la izquierda y quitar el color directo
const FeatureIcon = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  alignSelf: 'flex-start', // Asegura que el icono esté alineado a la izquierda
  
  '& svg': {
    width: '36px',
    height: '36px',
  },
  
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(2),
    '& svg': {
      width: '28px',
      height: '28px',
    },
  },
}));

// Nuevo componente para envolver iconos con gradiente
const GradientIcon = ({ children }) => {
  const iconId = React.useId();
  
  return (
    <Box sx={{ position: 'relative' }}>
      <svg width="0" height="0">
        <defs>
          <linearGradient id={`icon-gradient-${iconId}`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#2C5282" />
            <stop offset="50%" stopColor="#234876" />
            <stop offset="100%" stopColor="#1A365D" />
          </linearGradient>
        </defs>
      </svg>
      {React.cloneElement(children, {
        style: { stroke: `url(#icon-gradient-${iconId})` }
      })}
    </Box>
  );
};

const NavigationButtons = styled(Box)(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(2),
  bottom: theme.spacing(-6),
  display: 'flex',
  gap: theme.spacing(1),
  zIndex: 10, // Asegurar que esté sobre otros elementos
  [theme.breakpoints.up('sm')]: {
    right: theme.spacing(3),
  },
  [theme.breakpoints.up('lg')]: {
    right: theme.spacing(4),
  },
}));

// Botones de navegación actualizados con los colores específicos
const NavButton = styled(IconButton)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : (theme.palette.mode === 'dark' ? '#1c1c1e' : '#f2f2f7'),
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    color: isWallpaperMode ? '#FFFFFF' : (theme.palette.mode === 'dark' ? '#ffffff' : '#000000'),
    '&:hover': {
      backgroundColor: isWallpaperMode
        ? 'rgba(128, 128, 128, 0.5)'
        : (theme.palette.mode === 'dark' ? '#2c2c2e' : '#e2e2e7'),
    },

    [theme.breakpoints.down('sm')]: {
      width: '28px',
      height: '28px',
    },
  };
});

const GradientText = styled('span')({
  background: 'linear-gradient(to bottom, #2C5282, #234876, #1A365D)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  color: 'transparent',
  display: 'inline',
  fontSize: 'inherit',
  fontWeight: 'inherit',
  lineHeight: 'inherit'
});

const AGTLegalSection = ({ onOpenAGTLegal, hasAGTLegal, isMobile, isTablet, isAvailable = false }) => {
  const featuresRef = useRef(null);
  
  // Características de AGT Legal con enfoque en servicios jurídicos
  const legalFeatures = [
    {
      icon: <Scale />,
      title: "Get expert legal guidance for common situations"
    },
    {
      icon: <FileText />,
      title: "Generate legal documents and contracts"
    },
    {
      icon: <FileCheck />,
      title: "Review and analyze existing legal agreements"
    },
    {
      icon: <CalendarClock />,
      title: "Prepare effectively for meetings with attorneys"
    },
    {
      icon: <Search />,
      title: "Search and understand relevant laws and regulations"
    },
    {
      icon: <Shield />,
      title: "Protect your rights in disputes and claims"
    },
    {
      icon: <Landmark />,
      title: "Navigate complex legal processes with confidence"
    },
    {
      icon: <BookOpen />,
      title: "Access simplified explanations of legal concepts"
    }
  ];

  // Función para manejar el scroll horizontal
  const handleScroll = (direction) => {
    if (featuresRef.current) {
      // Ajustar el ancho de la tarjeta según el tamaño de la pantalla
      let cardWidth = 316; // Desktop default
      if (isTablet) cardWidth = 290;
      if (isMobile) cardWidth = 260;
      
      const gap = isMobile ? 16 : isTablet ? 24 : 32;
      const scrollAmount = cardWidth + gap;
      
      featuresRef.current.scrollBy({
        left: direction * scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <Box id="product-section-legal" sx={{ mb: 6, overflow: 'visible' }}>
      {/* Título de la sección fuera del contenedor de características */}
      <Box sx={{ 
        padding: '0 0 2rem',
        marginLeft: '0',
        marginRight: { xs: 0, sm: 3, lg: 4 }
      }}>
        <FeaturesSectionTitle>
          AGT Legal. <GradientText>Expertise at your service.</GradientText>
        </FeaturesSectionTitle>
      </Box>
      
      {/* Sección de características con scroll horizontal */}
      <FeaturesSection>
        <Box position="relative" sx={{ overflow: 'visible' }}>
          <FeatureCardsContainer ref={featuresRef}>
            {/* Tarjeta de acción con imagen de fondo */}
            <ActionCard>
              <ActionCardHeader>
                <FeatureCardTitle sx={{ color: '#ffffff' }}>AGT Legal</FeatureCardTitle>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: '#ffffff',
                    textAlign: 'left',
                    position: 'relative',
                    zIndex: 1,
                    fontSize: isMobile ? '0.875rem' : '1rem',
                    fontWeight: 500,
                    mt: 1
                  }}
                >
                  Legal solutions simplified.
                </Typography>
              </ActionCardHeader>
              
              <ActionCardFooter>
                {hasAGTLegal ? (
                  <ProStatusButton 
                    variant="contained" 
                    disableElevation
                  >
                    Yours
                  </ProStatusButton>
                ) : !isAvailable ? (
                  <NotAvailableButton 
                    variant="contained" 
                    disableElevation
                  >
                    Not Available
                  </NotAvailableButton>
                ) : (
                  <StyledButton 
                    variant="contained" 
                    onClick={onOpenAGTLegal}
                    disableElevation
                  >
                    <ButtonContent>
                      Buy
                    </ButtonContent>
                  </StyledButton>
                )}
              </ActionCardFooter>
            </ActionCard>
            
            {/* Tarjetas de características con iconos y textos estilo Apple */}
            {legalFeatures.map((feature, index) => (
              <FeatureCard key={index}>
                <FeatureIcon>
                  <GradientIcon>
                    {feature.icon}
                  </GradientIcon>
                </FeatureIcon>
                <FeatureCardTitle>{feature.title}</FeatureCardTitle>
              </FeatureCard>
            ))}
          </FeatureCardsContainer>
          
          <NavigationButtons>
            <NavButton onClick={() => handleScroll(-1)}>
              <ChevronLeft size={isMobile ? 14 : 16} />
            </NavButton>
            <NavButton onClick={() => handleScroll(1)}>
              <ChevronRight size={isMobile ? 14 : 16} />
            </NavButton>
          </NavigationButtons>
        </Box>
      </FeaturesSection>
    </Box>
  );
};

export default AGTLegalSection;